import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { inr } from '@/lib/loan';

const STATUSES = ['received', 'under_review', 'approved', 'disbursed', 'rejected'];

const STATUS_CLS = {
  received: 'bg-white/10 text-slate-300',
  under_review: 'bg-gold/15 text-gold',
  approved: 'bg-pulse/15 text-pulse',
  disbursed: 'bg-pulse text-ink',
  rejected: 'bg-red-500/15 text-red-300',
};

export default function ApplicationsTable({ apps, onPatch }) {
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  const toggle = (ref, currentNote) => {
    setNote(open === ref ? '' : currentNote || '');
    setOpen(open === ref ? null : ref);
  };

  const changeStatus = async (ref, status) => {
    setBusy(ref);
    try {
      await onPatch(ref, { status });
    } finally {
      setBusy('');
    }
  };

  const saveNote = async (app) => {
    setBusy(app.reference_id);
    try {
      await onPatch(app.reference_id, { status: app.status, note });
    } finally {
      setBusy('');
    }
  };

  const fmtDate = (iso) =>
    new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

  if (!apps.length) {
    return (
      <div className="mt-8 rounded-2xl border border-white/10 bg-panel/60 p-12 text-center" data-testid="admin-empty">
        <p className="text-sm text-slate-500">No applications match. New submissions appear here instantly.</p>
      </div>
    );
  }

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-white/10" data-testid="admin-applications-table">
      <div className="hidden grid-cols-[1.1fr_1.3fr_0.9fr_0.7fr_1fr_0.3fr] gap-3 border-b border-white/10 bg-abyss/70 px-5 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 lg:grid">
        <span>Reference</span><span>Applicant</span><span>Amount</span><span>Tenure</span><span>Status</span><span />
      </div>
      {apps.map((a) => (
        <div key={a.reference_id} className="border-b border-white/5 last:border-0" data-testid={`admin-app-row-${a.reference_id}`}>
          <div className="grid grid-cols-2 gap-3 px-5 py-4 transition-colors hover:bg-white/[0.02] lg:grid-cols-[1.1fr_1.3fr_0.9fr_0.7fr_1fr_0.3fr] lg:items-center">
            <div>
              <p className="font-mono text-sm font-bold text-pulse">{a.reference_id}</p>
              <p className="mt-0.5 text-[11px] text-slate-600">{fmtDate(a.created_at)}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{a.full_name}</p>
              <p className="mt-0.5 font-mono text-[11px] text-slate-500">{a.phone} · {a.city || '—'}</p>
            </div>
            <p className="font-mono text-sm font-semibold text-white">{inr(a.amount)}</p>
            <p className="font-mono text-sm text-slate-300">{a.tenure_days}d</p>
            <div className="flex items-center gap-2">
              <select
                data-testid={`admin-status-select-${a.reference_id}`}
                value={a.status}
                onChange={(e) => changeStatus(a.reference_id, e.target.value)}
                className={`w-full cursor-pointer rounded-full border-0 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wide outline-none ${STATUS_CLS[a.status] || 'bg-white/10 text-slate-300'}`}
                aria-label={`Status for ${a.reference_id}`}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s} className="bg-ink text-white">{s}</option>
                ))}
              </select>
              {busy === a.reference_id && <span className="h-2 w-2 animate-pulse rounded-full bg-pulse" data-testid={`admin-busy-${a.reference_id}`} />}
            </div>
            <button
              data-testid={`admin-expand-${a.reference_id}`}
              onClick={() => toggle(a.reference_id, a.note)}
              className="justify-self-end rounded-lg border border-white/10 p-2 text-slate-400 transition-colors hover:border-pulse/40 hover:text-pulse"
              aria-label="Toggle details"
            >
              <ChevronDown size={15} className={`transition-transform ${open === a.reference_id ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <AnimatePresence>
            {open === a.reference_id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden bg-ink/60"
                data-testid={`admin-details-${a.reference_id}`}
              >
                <div className="grid gap-x-8 gap-y-2 border-t border-white/5 px-5 py-5 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    ['Email', a.email],
                    ['Employment', a.employment_type],
                    ['Monthly income', inr(a.monthly_income)],
                    ['Salary date', `Day ${a.salary_date}`],
                    ['PAN', a.pan_number],
                    ['Aadhaar (last 4)', a.aadhaar_last4],
                    ['Bank', `${a.bank_name} · ****${a.account_number.slice(-4)}`],
                    ['IFSC', a.ifsc],
                    ['Purpose', a.purpose],
                    ['Interest', inr(a.quote?.interest ?? 0)],
                    ['Fee + GST', inr((a.quote?.processing_fee ?? 0) + (a.quote?.gst_on_fee ?? 0))],
                    ['Total repayment', inr(a.quote?.total_repayment ?? 0)],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-3">
                      <span className="text-slate-500">{label}</span>
                      <span className="text-right font-medium text-slate-200">{value}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-3 border-t border-white/5 px-5 py-4 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Note visible to applicant on the tracker
                    </label>
                    <textarea
                      data-testid={`admin-note-input-${a.reference_id}`}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      placeholder="e.g. Salary slip needed before approval"
                      className="w-full rounded-lg border border-white/10 bg-ink/70 px-4 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-slate-600 focus:border-pulse/70"
                    />
                  </div>
                  <button
                    data-testid={`admin-save-note-${a.reference_id}`}
                    onClick={() => saveNote(a)}
                    className="rounded-lg bg-pulse px-6 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-pulse-dim"
                  >
                    Save note
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}
